import { fetchBot } from '@/services/services';
import { useCallback, useEffect, useState } from 'react';
import { getDateRange } from '../utils/common';
import { mergeOverlappingWindows } from '../utils/availability';
import { AvailabilityBackgroundEvent, UseFindAvailabilityFilters } from '../types';
import dayjs from 'dayjs';
import { View } from 'react-big-calendar';
import { useMedplum } from '@medplum/react';
import { FindAvailabilityRequest, FindAvailabilityWindow } from '@bariendo/lib/scheduling';
import { alert } from '@/services/alert';

export function useFindAvailability(
  date: Date,
  view: View,
  enabled: boolean,
  filters: UseFindAvailabilityFilters,
  timezone: string
) {
  const medplum = useMedplum();
  const [backgroundEvents, setBackgroundEvents] = useState<AvailabilityBackgroundEvent[]>([]);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);

  const getAvailabilityWindows = useCallback(async () => {
    if (!enabled) return;
    setIsAvailabilityLoading(true);
    const { practitionersRoles, roles, serviceTypes, locations } = filters;
    const { startDate, endDate } = getDateRange(date, view, timezone);
    const requestBody: FindAvailabilityRequest = {
      startTime: startDate,
      endTime: endDate,
      practitionersRoles,
      roles,
      serviceTypes,
      locations,
    };
    try {
      const response = await fetchBot<FindAvailabilityWindow[]>(medplum, 'find-availability', requestBody);
      const windows = view === 'day' ? response : mergeOverlappingWindows(response);
      setBackgroundEvents(
        windows.map((a) => ({
          start: dayjs(a.startTime).toDate(),
          end: dayjs(a.endTime).toDate(),
          appointmentType: 'availability',
          ...(a.resourceId && { resource: a.resourceId }),
        }))
      );
    } catch (error) {
      alert.error(
        'Failed to fetch availability windows: ' + (error instanceof Error ? error.message : 'Unknown error')
      );
      setBackgroundEvents([]);
    } finally {
      setIsAvailabilityLoading(false);
    }
  }, [medplum, date, view, enabled, filters, timezone]);

  useEffect(() => {
    getAvailabilityWindows();
  }, [getAvailabilityWindows]);

  const refresh = useCallback(() => {
    getAvailabilityWindows();
  }, [getAvailabilityWindows]);

  return {
    backgroundEvents,
    isAvailabilityLoading,
    refresh,
  };
}
